import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import { AnimatedIcon } from './AnimatedIcon';
import { ICON_STROKE_WIDTH } from './constants';

export type { IconName };

export const iconVariants = cva(
  ['text-inherit', 'inline-flex', 'aspect-ratio-square', 'items-center', 'justify-center', 'transition-colors', 'shrink-0', 'outline-none'],
  {
    variants: {
      /** The size of the icon. */
      size: {
        xs: 'size-3 min-w-3 min-h-3 max-w-3 max-h-3', // 12px
        sm: 'size-4 min-w-4 min-h-4 max-w-4 max-h-4', // 16px
        md: 'size-5 min-w-5 min-h-5 max-w-5 max-h-5', // 20px
        lg: 'size-6 min-w-6 min-h-6 max-w-6 max-h-6', // 24px
        xl: 'size-8 min-w-8 min-h-8 max-w-8 max-h-8', // 32px
      },
      /** The semantic color of the icon. */
      color: {
        primary: 'stroke-primary-icon',
        success: 'stroke-success-icon',
        warning: 'stroke-warning-icon',
        error: 'stroke-error-icon',
        info: 'stroke-info-icon',
        default: 'stroke-default-icon',
        muted: 'stroke-default-icon-muted',
        accent1: 'stroke-accent-1-icon',
        accent2: 'stroke-accent-2-icon',
        inherit: 'stroke-current',
        disabled: 'stroke-disabled-foreground',
      },
    },
  },
);

export type IconProps = React.ComponentProps<'svg'> &
  VariantProps<typeof iconVariants> & {
    /** `'hover'` plays on hover/focus; `'mount'` plays on mount; `false` explicitly disables animation (equivalent to omitting the prop). Only valid for `AnimatedIconName` values — others fall back to a static icon with a `console.warn`. */
    animate?: 'mount' | 'hover' | false;
    /** When `animate='hover'`, pass `true` on mouseenter/focus and `false` on mouseleave/blur to drive the animation from the parent container (this is how Spellbook's own `Button`/`IconButton`/`NavLink`/`ToggleButton`/`ToggleIconButton` do it internally). Leave `undefined` to use the icon's built-in hover detection. */
    animating?: boolean;
    /** Lucide icon name in kebab-case (e.g. `arrow-right`). Defaults to `square-dashed`. Browse names at https://lucide.dev/icons/ */
    name?: IconName;
  };

/** The full set of values accepted by the `animate` prop on `Icon`. Use this instead of repeating the inline literal union across components and hooks. */
export type IconAnimateMode = IconProps['animate'];

/**
 * Decorative SVG icon component powered by the Lucide icon library.
 *
 * ### When to use
 * - Inside interactive elements like Button slots (`Button.LeadingIcon`, `Button.TrailingIcon`)
 * - As decorative enhancement alongside text content
 * - For status indicators within larger components (Alert, Badge, etc.)
 * - When the icon's meaning is already conveyed by surrounding text
 *
 * ### When NOT to use
 * - For clickable/interactive icons: use `IconButton` instead
 * - For toggle actions: use `ToggleIconButton` instead
 *
 * ### API
 * - `name`: Lucide icon name in kebab-case (e.g., `arrow-right`, `chevron-down`). See https://lucide.dev/icons/
 * - `size`: Icon dimensions: `xs`, `sm`, `md` (default), `lg`, `xl`
 * - `color`: Semantic stroke color: `inherit` (default, inherits from parent), `primary`, `success`, `warning`, `error`, `info`, `default`, `muted` (decorative icons only; below 3:1 contrast in light mode), `accent1`, `accent2`, `disabled`
 * - `animate`: `'hover'` plays the animation on hover or focus; `'mount'` plays on mount; `false` explicitly disables (equivalent to omitting the prop). Only valid for `AnimatedIconName` values — others fall back to static with a `console.warn`.
 * - `animating`: When `animate='hover'`, a parent container (e.g., `Button`, `NavLink`) can drive the animation imperatively by passing `true` on mouseenter/focus and `false` on mouseleave/blur. Leave `undefined` to use the icon's own built-in hover detection.
 * - All icons render at the design system stroke weight, exported as `ICON_STROKE_WIDTH`: never pass a custom `strokeWidth`
 *
 * ### Slot usage
 * Many Spellbook components have icon slots (e.g., `<Badge.LeadingIcon>`, `<Button.TrailingIcon>`,
 * `<DropdownMenu.ItemLeadingIcon>`) that accept `IconProps` directly — you do NOT need to pass
 * an `<Icon>` child. The slot renders the Icon automatically with props you provide:
 *
 * ```tsx
 * // ✅ Correct: pass IconProps directly to the slot
 * <Badge.LeadingIcon name="star" color="primary" />
 *
 * // ❌ Incorrect: don't wrap in Icon component
 * <Badge.LeadingIcon><Icon name="star" /></Badge.LeadingIcon>
 * ```
 *
 * To check what a slot renders by default, refer to the parent component's "Composition (slots)"
 * section in its JSDoc (e.g., Badge documents that `<Badge.LeadingIcon>` renders an Icon with
 * `size="sm"` and `color="default"` by default).
 *
 * To override the default render, use `overrideDefault={true}` with custom children.
 *
 * ### Animated icons
 * - Only names in `ANIMATED_ICON_NAMES` (`arrow-up`, `bookmark`, `circle-help`, `graduation-cap`, `history`, `layers`, `message-circle-plus`, `route`, `users`, `wrench`) support `animate`; the components live in `./animated/` and are owned locally
 * - The `motion` dependency is lazy-loaded on the first animated mount: static icons (the vast majority) pay no bundle or client-boundary cost
 * - Animated icons render inside a wrapper element that receives the `size` and `color` variant classes; the SVG fills its container and inherits the wrapper's stroke, so `size` and `color` work identically to static icons
 * - When a part of an icon rotates about anything other than its own centre, put the pivot in the variant as `originX`/`originY` (see `HistoryIcon`), not in `style.transformOrigin`: motion writes its own `transform-origin` next to every `transform` it sets, so a style-declared pivot holds at rest and is replaced the moment the animation runs
 * - Icons are usually rendered at `sm` (16px), where a few degrees of rotation is sub-pixel. Check an animation at the size it ships at, not in the story at `xl`
 *
 * ### Accessibility
 * - Renders with `role="presentation"` by default, making it invisible to screen readers
 * - For decorative icons (most cases), no additional attributes needed
 * - For meaningful icons without adjacent text, override with `role="img"` and add `aria-label`
 * - Animated variants mark the inner SVG `aria-hidden="true"` (matching lucide-react); pass a11y attributes on the Icon itself, they land on the wrapper
 * - Users with `prefers-reduced-motion: reduce` always receive the static icon, regardless of `animate`
 *
 * ### Icon reference
 * Browse 1500+ icons at https://lucide.dev/icons/
 *
 * @see {@link https://lucide.dev/icons/ | Lucide Icons} for available icon names
 */
export const Icon: React.FC<IconProps> = ({
  animate,
  animating,
  className,
  color = 'inherit',
  name = 'square-dashed',
  role = 'presentation',
  size = 'md',
  ...props
}) => {
  const resolvedClass = cn(iconVariants({ size, color }), className);

  if (!animate) {
    // Pure static path — no hooks, no client boundary. The vast majority of Icon usages.
    return (
      <DynamicIcon
        data-spellbook-name="icon"
        className={resolvedClass}
        name={name}
        role={role}
        strokeWidth={ICON_STROKE_WIDTH}
        {...props}
      />
    );
  }

  // Delegate to the stateful client component. Only icons with animate set pay this cost.
  return (
    <AnimatedIcon
      data-spellbook-name="icon"
      animate={animate}
      animating={animating}
      name={name}
      role={role}
      resolvedClass={resolvedClass}
      {...props}
    />
  );
};
